import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';

type RoleSlotState = Record<string, boolean>;
type RoleState = { selected: boolean; slots: RoleSlotState };

function buildVirtualState(contributor: ContributorDto): {
  contributorSelected: boolean;
  roles: Record<string, RoleState>;
} {
  const roles: Record<string, RoleState> = {};
  for (const role of contributor.roles) {
    if ((role as { deleted?: boolean }).deleted) continue;
    const slots: RoleSlotState = {};
    for (const slot of role.slots ?? []) {
      if (slot.deleted === true) continue;
      slots[slot.id] = slot.stagedForInvite === true;
    }
    roles[role.id] = {
      selected: role.stagedForInvite === true,
      slots,
    };
  }
  return {
    contributorSelected: contributor.stagedForInvite === true,
    roles,
  };
}

/** Prefer the role slot’s `id` from the DTO so virtual keys match PATCH URLs (hint may differ by string coercion). */
export function resolveCanonicalRoleSlotId(
  contributor: ContributorDto,
  roleId: string,
  slotIdHint: string,
): string | undefined {
  const role = contributor.roles.find((r) => r.id === roleId);
  const slot = role?.slots?.find(
    (s) => s.deleted !== true && (s.id === slotIdHint || String(s.id) === String(slotIdHint)),
  );
  return slot?.id;
}

/** Contributor staged iff any role or any slot under any role is staged. */
function syncContributorFromChildren(v: { contributorSelected: boolean; roles: Record<string, RoleState> }) {
  const anyStaged = Object.values(v.roles).some((r) => r.selected || Object.values(r.slots).some(Boolean));
  v.contributorSelected = anyStaged;
}

/**
 * Staging cascade for `stagedForInvite` (list checkboxes and widget OUTBOX toggles).
 * Returns PATCH diffs vs current DTO.
 */
export function computeStagedForInvitePatches(
  contributor: ContributorDto,
  action: {
    isSelected: boolean;
    contributorId: string;
    roleId?: string;
    slotId?: string;
  },
): {
  contributorId: string;
  contributorStaged?: boolean;
  roles: { roleId: string; stagedForInvite: boolean }[];
  slots: { roleId: string; slotId: string; stagedForInvite: boolean }[];
} {
  const v = buildVirtualState(contributor);

  if (action.slotId && action.roleId) {
    const { roleId } = action;
    const canonicalSlotId = resolveCanonicalRoleSlotId(contributor, roleId, action.slotId);
    if (!canonicalSlotId || !v.roles[roleId]) return { contributorId: contributor.id, roles: [], slots: [] };

    v.roles[roleId].slots[canonicalSlotId] = action.isSelected;
    if (action.isSelected) {
      // Slot depends on role: turning a slot on requires the role to be staged.
      v.roles[roleId].selected = true;
    }
    // Slot off does not change the role's staged state.
    syncContributorFromChildren(v);
  } else if (action.roleId) {
    const roleId = action.roleId;
    if (!v.roles[roleId]) return { contributorId: contributor.id, roles: [], slots: [] };

    if (action.isSelected) {
      // Role on does not auto-stage slots; keep existing slot flags.
      v.roles[roleId].selected = true;
    } else {
      // Role off clears all slots under this role.
      v.roles[roleId].selected = false;
      for (const sid of Object.keys(v.roles[roleId].slots)) {
        v.roles[roleId].slots[sid] = false;
      }
    }
    syncContributorFromChildren(v);
  } else {
    if (action.isSelected) {
      // Contributor on: stage every role; slots unchanged (role on does not force slots on).
      v.contributorSelected = true;
      for (const rid of Object.keys(v.roles)) {
        v.roles[rid].selected = true;
      }
    } else {
      // Contributor off: clear all roles and slots.
      v.contributorSelected = false;
      for (const rid of Object.keys(v.roles)) {
        v.roles[rid].selected = false;
        for (const sid of Object.keys(v.roles[rid].slots)) {
          v.roles[rid].slots[sid] = false;
        }
      }
    }
  }

  const roles: { roleId: string; stagedForInvite: boolean }[] = [];
  const slots: { roleId: string; slotId: string; stagedForInvite: boolean }[] = [];

  for (const role of contributor.roles) {
    if ((role as { deleted?: boolean }).deleted) continue;
    const vs = v.roles[role.id];
    if (!vs) continue;
    if (vs.selected !== (role.stagedForInvite === true)) {
      roles.push({ roleId: role.id, stagedForInvite: vs.selected });
    }
    for (const slot of role.slots ?? []) {
      if (slot.deleted === true) continue;
      const next = vs.slots[slot.id] ?? false;
      if (next !== (slot.stagedForInvite === true)) {
        slots.push({ roleId: role.id, slotId: slot.id, stagedForInvite: next });
      }
    }
  }

  let contributorStaged: boolean | undefined;
  if (v.contributorSelected !== (contributor.stagedForInvite === true)) {
    contributorStaged = v.contributorSelected;
  }

  return {
    contributorId: contributor.id,
    ...(contributorStaged !== undefined ? { contributorStaged } : {}),
    roles,
    slots,
  };
}

export type StagedForInvitePlan = ReturnType<typeof computeStagedForInvitePatches>;

/** Applies PATCHes from `computeStagedForInvitePatches` (same as contributors list). */
export async function applyStagedForInvitePlan(
  plan: StagedForInvitePlan,
  deps: {
    patchContributor: (args: { id: string; stagedForInvite: boolean }) => Promise<unknown>;
    patchContributorRole: (args: {
      contributorId: string;
      roleId: string;
      stagedForInvite: boolean;
    }) => Promise<unknown>;
    patchContributorRoleSlot: (args: {
      contributorId: string;
      roleId: string;
      slotId: string;
      stagedForInvite: boolean;
    }) => Promise<unknown>;
  },
): Promise<void> {
  const { contributorId } = plan;
  const tasks: Promise<unknown>[] = [
    ...plan.slots.map((s) =>
      deps.patchContributorRoleSlot({
        contributorId,
        roleId: s.roleId,
        slotId: s.slotId,
        stagedForInvite: s.stagedForInvite,
      }),
    ),
    ...plan.roles.map((r) =>
      deps.patchContributorRole({
        contributorId,
        roleId: r.roleId,
        stagedForInvite: r.stagedForInvite,
      }),
    ),
  ];
  if (plan.contributorStaged !== undefined) {
    tasks.push(deps.patchContributor({ id: contributorId, stagedForInvite: plan.contributorStaged }));
  }
  await Promise.all(tasks);
}
